import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { Box, Button, Grow, Typography, useTheme } from '@mui/material';
import { darken } from '@mui/system';
import React from 'react';
import { TransitionGroup } from 'react-transition-group';
import { IconKind, IconWrapper } from '../../components/icon/IconWrapper.tsx';
import { SubprojectBuildingDetails } from '../../stores/SubprojectStore.ts';

interface IncludedSystemsProps {
  subproject?: SubprojectBuildingDetails;
  onUpdate: React.Dispatch<
    React.SetStateAction<SubprojectBuildingDetails | undefined>
  >;
  readOnly?: boolean;
}

export const IncludedSystems: React.FC<IncludedSystemsProps> = ({
  subproject,
  onUpdate,
  readOnly,
}) => {
  const theme = useTheme();
  const { technologyCategoriesBuildings } = useEnumHelpers();

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          paddingTop: 4,
        }}
      >
        <Typography
          variant={'h5'}
          sx={{ fontSize: 16, letterSpacing: 8, textTransform: 'uppercase' }}
        >
          Which systems will be included?
        </Typography>
        <Typography
          variant={'caption'}
          sx={{
            m: 2,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: 4,
            textTransform: 'uppercase',
          }}
        >
          Select all that apply
        </Typography>
      </Box>
      <Box
        component={TransitionGroup}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: 4,
          gap: 1,
          justifyContent: 'center',
          '& > :not(style)': {
            m: 1,
            width: 120,
            height: 120,
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
          },
        }}
      >
        {(Array.isArray(technologyCategoriesBuildings)
          ? technologyCategoriesBuildings
          : []
        )
          .filter((tech: any) => {
            // Only show "Specialized" for Food/Agriculture, Recreation, and Medical building types
            if (tech.name === 'Specialized') {
              const buildingUsages = subproject?.buildingUsages || [];
              return buildingUsages.some((usage: string) =>
                ['Food/Agriculture', 'Recreation', 'Medical'].includes(usage),
              );
            }
            return true;
          })
          .map((tech: any) => {
            // If subproject isn't provided, render an empty fragment
            if (!subproject) {
              return <React.Fragment key={tech.name} />;
            }
            // Fallback to an empty array if enabledTechnologyCategories is undefined.
            const currentTechCategories =
              subproject.enabledTechnologyCategories || [];

            // Check if this tech is selected by comparing the name
            const isSelected = currentTechCategories.includes(tech.name);

            return (
              <Grow key={tech.name}>
                <Button
                  variant={'contained'}
                  color={'primary'}
                  disabled={readOnly}
                  onClick={() => {
                    if (readOnly) return;
                    if (!subproject) return;

                    let updatedCategories: string[];
                    if (isSelected) {
                      // Remove this tech from the array
                      updatedCategories = currentTechCategories.filter(
                        (cat: string) => cat !== tech.name,
                      );
                    } else {
                      // Add this tech to the array (just the name, not the whole object)
                      updatedCategories = [
                        ...currentTechCategories,
                        tech.name,
                      ].sort((a, b) => {
                        const indexA = technologyCategoriesBuildings.findIndex(
                          (item) => item.name === a,
                        );
                        const indexB = technologyCategoriesBuildings.findIndex(
                          (item) => item.name === b,
                        );
                        return indexA - indexB;
                      });
                    }

                    onUpdate({
                      ...subproject,
                      enabledTechnologyCategories: updatedCategories,
                    });
                  }}
                  sx={{
                    borderRadius: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    width: 145,
                    height: 148,
                    '&.MuiButton-containedPrimary': {
                      backgroundColor: isSelected
                        ? 'secondary.main'
                        : 'info.main',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? 'success.main'
                          : darken(theme.palette.info.main, 0.15),
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                  >
                    <IconWrapper
                      icon={tech.name as IconKind}
                      color={'primary'}
                      sx={{
                        height: 55,
                        width: 55,
                        mb: 1,
                      }}
                    />
                    <Typography
                      variant={'subtitle2'}
                      color={'primary'}
                      sx={{ mt: 1, textTransform: 'uppercase', fontSize: 10 }}
                    >
                      {tech.description}
                    </Typography>
                  </Box>
                </Button>
              </Grow>
            );
          })}
      </Box>
    </React.Fragment>
  );
};
